/* =========================================================
   CART GIFT BOX
   Ticking the gift box checkbox in the cart drawer adds the
   configured gift box product (qty 1); unticking removes it.
   Uses the standard Ajax cart endpoints and re-renders the
   drawer in place WITHOUT calling CartDrawer.renderContents(),
   which would re-trigger the auto-open / ATC toast.
   window.GIFT_BOX is set in cart-drawer.liquid only when the
   feature is enabled AND a gift box product is assigned.
========================================================= */
(function () {
  if (window.__giftBoxBound) return;
  window.__giftBoxBound = true;

  const GIFT_PROPERTY = '_gift_box';
  let giftBoxBusy = false;

  function giftVariantId() {
    return window.GIFT_BOX && Number(window.GIFT_BOX.variantId);
  }

  // Only lines added by the gift box checkbox (tagged _gift_box). The same product
  // can be in the cart for another reason (e.g. bought from its product page) and
  // must never be ticked, removed or quantity-limited by this script.
  function isGiftLine(item) {
    return !!(item.properties && item.properties[GIFT_PROPERTY] === 'true');
  }

  // The gift box is limited to ONE unit per order: more than one gift line, or a
  // quantity other than 1 (e.g. raised on the /cart page or in another tab),
  // must be corrected back to a single unit.
  function needsGiftFix(cart) {
    const giftLines = cart.items.filter(isGiftLine);
    return giftLines.length > 1 || giftLines.some((line) => line.quantity !== 1);
  }

  function isCartPage() {
    return !!document.getElementById('main-cart-items');
  }

  function getCart() {
    return fetch(window.Shopify.routes.root + 'cart.js').then((r) => r.json());
  }

  function cartRequest(endpoint, payload) {
    return fetch(window.Shopify.routes.root + endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...payload,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    }).then((r) => {
      if (!r.ok) throw new Error('Gift box cart request failed: ' + r.status);
      return r.json();
    });
  }

  function sectionInnerHTML(html, selector) {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  // Swap the drawer + header bubble markup in place (drawer stays as-is, open or closed).
  function renderSections(sections) {
    if (!sections) return;

    const drawerInner = document.getElementById('CartDrawer');
    if (drawerInner && sections['cart-drawer']) {
      const html = sectionInnerHTML(sections['cart-drawer'], '#CartDrawer');
      if (html !== null) drawerInner.innerHTML = html;
    }

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      const html = sectionInnerHTML(sections['cart-icon-bubble'], '.shopify-section');
      if (html !== null) bubble.innerHTML = html;
    }

    // Keep the mystery box countdown running after a re-render, if it's in use.
    if (typeof window.startMysteryTimer === 'function') window.startMysteryTimer();
  }

  // Bring the cart in line with the desired state. Always reads the live cart
  // first, so repeated clicks can never add the gift box more than once.
  async function syncGiftBox(shouldHaveGift, cart) {
    cart = cart || (await getCart());
    const giftLines = cart.items.filter(isGiftLine);
    const hasOtherItems = cart.items.some((item) => !isGiftLine(item));

    // Never leave the gift box as the only thing in the cart.
    if (!hasOtherItems) shouldHaveGift = false;

    if (shouldHaveGift) {
      if (giftLines.length === 1 && giftLines[0].quantity === 1) return null;

      if (giftLines.length) {
        // Gift box present with a qty other than 1 (or split across lines) → normalise to one unit.
        const updates = {};
        giftLines.forEach((line, i) => { updates[line.key] = i === 0 ? 1 : 0; });
        return cartRequest('cart/update.js', { updates });
      }

      return cartRequest('cart/add.js', {
        items: [{ id: giftVariantId(), quantity: 1, properties: { [GIFT_PROPERTY]: 'true' } }]
      });
    }

    if (!giftLines.length) return null;

    const updates = {};
    giftLines.forEach((line) => { updates[line.key] = 0; });
    return cartRequest('cart/update.js', { updates });
  }

  // `fixOnly`: an automatic correction (not a checkbox click). On the /cart page the
  // line items aren't part of the drawer sections we re-render, so reload it to
  // show the corrected quantity and totals.
  async function runSync(shouldHaveGift, cart, checkbox, fixOnly = false) {
    if (giftBoxBusy || !giftVariantId()) return;
    giftBoxBusy = true;

    const wrapper = checkbox && checkbox.closest('[data-gift-box]');
    if (checkbox) checkbox.disabled = true;
    if (wrapper) wrapper.classList.add('is-loading');

    try {
      const result = await syncGiftBox(shouldHaveGift, cart);
      if (result && result.sections) {
        renderSections(result.sections);
        // cart/update.js returns the full cart; mirror cart.js's empty-state toggle.
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof result.item_count === 'number') {
          drawer.classList.toggle('is-empty', result.item_count === 0);
        }
        if (fixOnly && isCartPage()) window.location.reload();
      }
    } catch (err) {
      console.error('Gift box error:', err);
      // Restore the checkbox to match the real cart state.
      if (checkbox) checkbox.checked = !shouldHaveGift;
      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors && window.cartStrings) errors.textContent = window.cartStrings.error;
    } finally {
      giftBoxBusy = false;
      // Element may have been replaced by the re-render; only reset if still in the DOM.
      if (checkbox && checkbox.isConnected) checkbox.disabled = false;
      if (wrapper && wrapper.isConnected) wrapper.classList.remove('is-loading');
    }
  }

  // Delegated so it survives every drawer re-render. The checkbox lives in the
  // drawer footer, outside <cart-drawer-items>, so it never reaches the
  // existing quantity change handler.
  document.addEventListener('change', function (e) {
    const checkbox = e.target.closest && e.target.closest('.js-gift-box-checkbox');
    if (!checkbox) return;
    e.stopPropagation();

    if (giftBoxBusy) {
      checkbox.checked = !checkbox.checked;
      return;
    }
    trackGiftBoxClick(checkbox);
    runSync(checkbox.checked, null, checkbox);
  });

  // CleverTap "section_clicked" — same payload format as duo-product-add-to-cart
  // and the global .section-item-tracked handler (ga4-dataLayer.liquid).
  function trackGiftBoxClick(checkbox) {
    try {
      const wrapper = checkbox.closest('[data-gift-box]');
      if (!wrapper) return;
      const clevertapData = {
        position: '',
        page: wrapper.getAttribute('data-page') || '',
        path: wrapper.getAttribute('data-path') || '',
        section_name: wrapper.getAttribute('data-section-name') || '',
        section_title: wrapper.getAttribute('data-section-title') || '',
        item_type: 'gift_box',
        item_title: wrapper.getAttribute('data-item-title') || '',
        item_link: wrapper.getAttribute('data-item-link') || '',
        action: checkbox.checked ? 'checked' : 'unchecked',
        product_variant_id: checkbox.dataset.variantId || '',
        product_price: (Number(wrapper.getAttribute('data-price')) || 0) / 100
      };

      if (typeof clevertap !== 'undefined' && clevertap.event) {
        clevertap.event.push('section_clicked', clevertapData);
      }
      if (typeof dataLayer !== 'undefined') {
        dataLayer.push({ event: 'section_clicked', clevertap: clevertapData });
      }
      console.log('SECTION CLICKED (gift box) >>>>>>>>>>', clevertapData);
    } catch (err) {
      console.warn('gift box section_clicked:', err);
    }
  }

  // If the customer removes everything else, drop the orphaned gift box too.
  // cart.js publishes cartUpdate (with the fresh cart) for qty +/- and Remove.
  // subscribe()/PUB_SUB_EVENTS come from deferred scripts, so register once ready.
  function registerGiftBoxCartWatcher() {
    if (window.__giftBoxCartWatcherBound) return true;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) {
      return false;
    }
    window.__giftBoxCartWatcherBound = true;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      const cart = event && event.cartData;
      if (!cart || !Array.isArray(cart.items)) return;
      const hasGift = cart.items.some(isGiftLine);
      const hasOtherItems = cart.items.some((item) => !isGiftLine(item));
      if (hasGift && !hasOtherItems) runSync(false, cart, null, true);
      else if (hasGift && needsGiftFix(cart)) runSync(true, cart, null, true);
    });
    return true;
  }

  if (!registerGiftBoxCartWatcher()) {
    document.addEventListener('DOMContentLoaded', registerGiftBoxCartWatcher);
    window.addEventListener('load', registerGiftBoxCartWatcher);
  }

  // Page loaded with more than one gift box in the cart (flagged by cart-drawer.liquid,
  // so normal page loads make no extra request) → correct it back to one.
  if (window.GIFT_BOX && window.GIFT_BOX.needsFix) {
    runSync(true, null, null, true);
  }
})();
